import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { CartProvider } from "@/context/CartContext";
import { AuthCallback, AuthProvider } from "@/context/AuthContext";
import Account from "@/pages/Account";
import ResetPassword from "@/pages/ResetPassword";
import GiftCards from "@/pages/GiftCards";
import GiftBalance from "@/pages/GiftBalance";
import Bundle from "@/pages/Bundle";
import AdminCrew from "@/pages/admin/AdminCrew";
import AdminGiftCards from "@/pages/admin/AdminGiftCards";
import AdminBundles from "@/pages/admin/AdminBundles";
import { SettingsProvider } from "@/context/SettingsContext";
import DropPage from "@/pages/DropPage";
import Crew from "@/pages/Crew";
import SotmWall from "@/pages/sotm/SotmWall";
import AdminLookbook from "@/pages/admin/AdminLookbook";
import AdminRestock from "@/pages/admin/AdminRestock";
import AdminSettings from "@/pages/admin/AdminSettings";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { CartDrawer } from "@/components/layout/CartDrawer";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import Product from "@/pages/Product";
import Drops from "@/pages/Drops";
import Lookbook from "@/pages/Lookbook";
import About from "@/pages/About";
import Checkout from "@/pages/Checkout";
import NotFound from "@/pages/NotFound";
import SotmGallery from "@/pages/sotm/SotmGallery";
import SotmSubmit from "@/pages/sotm/SotmSubmit";
import SotmWinner from "@/pages/sotm/SotmWinner";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminSotm from "@/pages/admin/AdminSotm";
import AdminSubscribers from "@/pages/admin/AdminSubscribers";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminDrops from "@/pages/admin/AdminDrops";
import OrderSuccess from "@/pages/OrderSuccess";
import OrderLookup from "@/pages/OrderLookup";

const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 120);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
};

const AppRoutes = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/product/:slug" element={<Product />} />
            <Route path="/drops" element={<Drops />} />
            <Route path="/drops/:slug" element={<DropPage />} />
            <Route path="/crew" element={<Crew />} />
            <Route path="/account" element={<Account />} />
            <Route path="/account/reset" element={<ResetPassword />} />
            <Route path="/gift-cards" element={<GiftCards />} />
            <Route path="/gift-cards/balance" element={<GiftBalance />} />
            <Route path="/bundles/:slug" element={<Bundle />} />
            <Route path="/admin/crew-points" element={<AdminCrew />} />
            <Route path="/admin/gift-cards" element={<AdminGiftCards />} />
            <Route path="/admin/bundles" element={<AdminBundles />} />
            <Route path="/sus-of-the-month/wall" element={<SotmWall />} />
            <Route path="/admin/lookbook" element={<AdminLookbook />} />
            <Route path="/admin/restock-alerts" element={<AdminRestock />} />
            <Route path="/admin/settings" element={<AdminSettings />} />
            <Route path="/lookbook" element={<Lookbook />} />
            <Route path="/about" element={<About />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/cart" element={<Checkout />} />
            <Route path="/order/:id/success" element={<OrderSuccess />} />
            <Route path="/order-lookup" element={<OrderLookup />} />
            <Route path="/sus-of-the-month" element={<SotmGallery />} />
            <Route path="/sus-of-the-month/submit" element={<SotmSubmit />} />
            <Route path="/sus-of-the-month/vote/:id" element={<SotmGallery />} />
            <Route path="/v/:id" element={<SotmGallery />} />
            <Route path="/sus-of-the-month/:slug" element={<SotmWinner />} />
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/sus-of-the-month" element={<AdminSotm />} />
            <Route path="/admin/subscribers" element={<AdminSubscribers />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/orders" element={<AdminOrders />} />
            <Route path="/admin/products" element={<AdminProducts />} />
            <Route path="/admin/drops" element={<AdminDrops />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <SettingsProvider>
      <AuthProvider>
      <CartProvider>
        <ScrollManager />
        <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-[100] focus:bg-lime focus:p-2">Skip to content</a>
        <Header />
        <main id="main" className="min-h-[70vh]">
          <AppRoutes />
        </main>
        <Footer />
        <CartDrawer />
        <Toaster position="bottom-center" toastOptions={{ className: "!rounded-none !border-2 !border-[#111] !font-mono !bg-[#F4F3EC] !text-[#111]" }} />
      </CartProvider>
      </AuthProvider>
      </SettingsProvider>
    </BrowserRouter>
  );
}

export default App;
